import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, ShieldCheck, Check, Clock, UserCheck, UserPlus, KeyRound, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const EMPTY_NEW = { name: "", email: "", password: "", role: "member", approved: true };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);

  // create user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_NEW);
  const [saving, setSaving] = useState(false);

  // set-password (for a user) dialog
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");

  // change own password dialog
  const [selfOpen, setSelfOpen] = useState(false);
  const [selfForm, setSelfForm] = useState({ current_password: "", new_password: "" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try { await api.put(`/users/${id}/role`, { role }); toast.success("Rola zaktualizowana"); load(); }
    catch { toast.error("Błąd"); }
  };

  const del = async (id) => {
    if (!window.confirm("Usunąć użytkownika?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Usunięto"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  };

  const approve = async (id) => {
    try { await api.put(`/users/${id}/approve`); toast.success("Konto zatwierdzone — wysłano e-mail do członka"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`Utworzono konto ${form.role === "admin" ? "administratora" : "członka"}`);
      setCreateOpen(false); setForm(EMPTY_NEW); load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Błąd tworzenia konta"); }
    finally { setSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwValue.length < 8) { toast.error("Hasło musi mieć min. 8 znaków"); return; }
    setSaving(true);
    try {
      await api.put(`/users/${pwUser.id}/password`, { password: pwValue });
      toast.success(`Hasło dla ${pwUser.name} zmienione`);
      setPwUser(null); setPwValue("");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Błąd"); }
    finally { setSaving(false); }
  };

  const changeSelf = async (e) => {
    e.preventDefault();
    if (selfForm.new_password.length < 8) { toast.error("Nowe hasło musi mieć min. 8 znaków"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", selfForm);
      toast.success("Twoje hasło zostało zmienione");
      setSelfOpen(false); setSelfForm({ current_password: "", new_password: "" });
    } catch (e2) { toast.error(e2.response?.data?.detail || "Błąd"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Użytkownicy i uprawnienia</h2>
          <p className="mt-1 text-sm text-muted-foreground">Zatwierdzaj konta, dodawaj administratorów i zmieniaj hasła</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSelfOpen(true)} className="gap-1.5" data-testid="self-password-btn">
            <Lock className="h-4 w-4" /> Zmień moje hasło
          </Button>
          <Button onClick={() => { setForm(EMPTY_NEW); setCreateOpen(true); }} className="gap-1.5 bg-ztk-blue" data-testid="user-create-btn">
            <UserPlus className="h-4 w-4" /> Dodaj konto
          </Button>
        </div>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Użytkownik</th>
              <th className="px-4 py-3 font-medium">E-mail</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Rola</th>
              <th className="px-4 py-3 font-medium text-right">Akcje</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border bg-card" data-testid={`admin-user-${u.id}`}>
                <td className="px-4 py-3 font-medium text-ztk-navy dark:text-white">
                  <span className="flex items-center gap-1.5">{u.role === "admin" && <ShieldCheck className="h-4 w-4 text-ztk-blue" />}{u.name}</span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  {u.approved
                    ? <Badge className="gap-1 bg-emerald-500 hover:bg-emerald-500"><Check className="h-3 w-3" /> Aktywny</Badge>
                    : <Badge variant="secondary" className="gap-1 text-ztk-yellow"><Clock className="h-3 w-3" /> Oczekuje</Badge>}
                </td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === me.id}>
                    <SelectTrigger className="h-8 w-32" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Członek</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {!u.approved && (
                      <Button size="sm" onClick={() => approve(u.id)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid={`user-approve-${u.id}`}>
                        <UserCheck className="h-3.5 w-3.5" /> Zatwierdź
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => { setPwUser(u); setPwValue(""); }} className="text-ztk-blue" title="Zmień hasło" data-testid={`user-password-${u.id}`}>
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    {u.id !== me.id && (
                      <Button size="icon" variant="ghost" onClick={() => del(u.id)} className="text-destructive" data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                    {u.id === me.id && <Badge variant="secondary">To Ty</Badge>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE USER */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Nowe konto</DialogTitle></DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div><Label>Imię i nazwisko</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="new-user-name" /></div>
            <div><Label>E-mail</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="new-user-email" /></div>
            <div><Label>Hasło (min. 8 znaków)</Label><Input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="new-user-password" /></div>
            <div>
              <Label>Rola</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Członek</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="approved" checked={form.approved} onCheckedChange={(v) => setForm({ ...form, approved: !!v })} data-testid="new-user-approved" />
              <Label htmlFor="approved" className="cursor-pointer">Konto zatwierdzone (aktywne od razu)</Label>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-ztk-blue" data-testid="new-user-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Utwórz konto</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SET PASSWORD FOR USER */}
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Zmień hasło: {pwUser?.name}</DialogTitle></DialogHeader>
          <form onSubmit={savePassword} className="space-y-4">
            <div><Label>Nowe hasło (min. 8 znaków)</Label><Input required type="password" minLength={8} value={pwValue} onChange={(e) => setPwValue(e.target.value)} className="mt-1.5" data-testid="set-password-input" /></div>
            <p className="text-xs text-muted-foreground">Użytkownik zostanie wylogowany ze wszystkich urządzeń.</p>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-ztk-blue" data-testid="set-password-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Zapisz hasło</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CHANGE OWN PASSWORD */}
      <Dialog open={selfOpen} onOpenChange={setSelfOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Zmień moje hasło</DialogTitle></DialogHeader>
          <form onSubmit={changeSelf} className="space-y-4">
            <div><Label>Aktualne hasło</Label><Input required type="password" value={selfForm.current_password} onChange={(e) => setSelfForm({ ...selfForm, current_password: e.target.value })} className="mt-1.5" data-testid="self-current-password" /></div>
            <div><Label>Nowe hasło (min. 8 znaków)</Label><Input required type="password" minLength={8} value={selfForm.new_password} onChange={(e) => setSelfForm({ ...selfForm, new_password: e.target.value })} className="mt-1.5" data-testid="self-new-password" /></div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-ztk-blue" data-testid="self-password-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Zmień hasło</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
